import {DYNAMIC_VALUE_CHARS, getFilterValue} from '@github-ui/filter-core'
import {type BlockValueItem, type FilterBlock, FilterOperator} from '@github-ui/filter-core'
import {clsx} from 'clsx'
import {memo, type ReactNode} from 'react'

import styles from './StyledInput.module.css'
import {Delimiter, KeyBlock} from './StyledInputBlocks'

const OneCharModifiers: FilterOperator[] = [
  FilterOperator.GreaterThan,
  FilterOperator.LessThan,
  FilterOperator.Before,
  FilterOperator.After,
]

const TwoCharModifiers: FilterOperator[] = [
  FilterOperator.GreaterThanOrEqualTo,
  FilterOperator.LessThanOrEqualTo,
  FilterOperator.BeforeAndIncluding,
  FilterOperator.AfterAndIncluding,
]

interface FilterExpressionBlockProps {
  block: FilterBlock
  /** Whether this block is the query's active block (caret currently within it). */
  isActiveBlock: boolean
  inputFocused: boolean
  filterDelimiter: string
  valueDelimiter: string
}

function renderValueElement(
  block: FilterBlock,
  blockValue: BlockValueItem,
  isActiveBlock: boolean,
  inputFocused: boolean,
  position?: number,
): ReactNode {
  const {key, operator} = block
  const {value, valid} = blockValue
  const keyBase = `${key.value}-${position ? `${position}-` : ''}${getFilterValue(value)}`

  if (valid) {
    let dynamicPosition = 0
    if (OneCharModifiers.includes(operator)) {
      dynamicPosition = 1
    } else if (TwoCharModifiers.includes(operator)) {
      dynamicPosition = 2
    }

    const dynamicValueChar = getFilterValue(value)?.charAt(dynamicPosition)
    if (dynamicValueChar && DYNAMIC_VALUE_CHARS.includes(dynamicValueChar)) {
      return (
        <span
          key={`filter-value-${keyBase}`}
          data-type="filter-value"
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          className={clsx(`valid-filter-value`, styles.Box_0)}
        >
          {getFilterValue(value)}
        </span>
      )
    } else {
      return (
        <span
          key={`filter-value-${keyBase}`}
          data-type="filter-value"
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          className={clsx(`valid-filter-value`, styles.Box_1)}
        >
          {getFilterValue(value)}
        </span>
      )
    }
  } else if (valid !== undefined && ((valid === false && !isActiveBlock) || !inputFocused)) {
    return (
      <span
        key={`filter-value-${keyBase}`}
        data-type="filter-value"
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        className={clsx(`invalid-filter-value`, styles.Box_2)}
      >
        {getFilterValue(value)}
      </span>
    )
  }

  return (
    <span key={`filter-value-${keyBase}`} data-type="filter-value">
      {getFilterValue(value)}
    </span>
  )
}

/**
 * Renders a single parsed filter expression (e.g. `is:open`, `author:a,b`, `created:1..2`) as a
 * colorized `filter-expression` span. Extracted into a `memo` component — keyed on a content
 * signature rather than object identity — so that on each keystroke only the block the user is
 * actually editing rebuilds its span subtree. Every parse/validate pass allocates brand-new block
 * objects, so without this memo the parent would recreate every block's inner elements on every
 * keystroke (the dominant `jsx` element-creation cost in the typing profile).
 */
function FilterExpressionBlockImpl({
  block,
  isActiveBlock,
  inputFocused,
  filterDelimiter,
  valueDelimiter,
}: FilterExpressionBlockProps) {
  const {key, operator, value} = block

  const isInvalidFilterKey = key.valid === false && (!isActiveBlock || !inputFocused)

  let filterValues: ReactNode[] = []

  if (operator === FilterOperator.Between) {
    const value1 = value.values[0]
    const value2 = value.values[1]
    if (value1) {
      filterValues.push(renderValueElement(block, value1, isActiveBlock, inputFocused, 1))
    }
    filterValues.push(
      /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
      <span key={`${key.value}-between-delimiter`} className={`delimiter`}>
        ..
      </span>,
    )
    if (value2) {
      filterValues.push(renderValueElement(block, value2, isActiveBlock, inputFocused, 2))
    }
  } else {
    filterValues = value.values.map((v, i) => {
      return (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <span key={`${key.value}-value-${i}`}>
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          {i > 0 && <span className={`delimiter`}>{valueDelimiter}</span>}
          {renderValueElement(block, v, isActiveBlock, inputFocused)}
        </span>
      )
    })
  }

  return (
    <span data-type="filter-expression">
      <KeyBlock text={key.value} className={clsx(isInvalidFilterKey && styles.KeyBlock_0)} />
      <Delimiter delimiter={filterDelimiter} />
      {filterValues}
    </span>
  )
}

/**
 * Two blocks render identically when their text (`raw`), validity flags, active/focus state and
 * delimiters all match. `raw` already encodes the key, operator and value text, so comparing it
 * alongside the validity flags (which come from the validation pass and are NOT part of `raw`) is a
 * complete representation of this block's rendered output. Returning `true` lets `memo` skip
 * rebuilding the span subtree for blocks the user is not editing.
 */
function arePropsEqual(prev: FilterExpressionBlockProps, next: FilterExpressionBlockProps): boolean {
  if (
    prev.isActiveBlock !== next.isActiveBlock ||
    prev.inputFocused !== next.inputFocused ||
    prev.filterDelimiter !== next.filterDelimiter ||
    prev.valueDelimiter !== next.valueDelimiter
  ) {
    return false
  }

  const a = prev.block
  const b = next.block
  if (a.raw !== b.raw || a.operator !== b.operator || a.key.valid !== b.key.valid) {
    return false
  }

  const aValues = a.value.values
  const bValues = b.value.values
  if (aValues.length !== bValues.length) return false
  for (let i = 0; i < aValues.length; i++) {
    if (aValues[i]!.value !== bValues[i]!.value || aValues[i]!.valid !== bValues[i]!.valid) {
      return false
    }
  }

  return true
}

export const FilterExpressionBlock = memo(FilterExpressionBlockImpl, arePropsEqual)
